import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { getLocalApiUrl, authFetch } from '../config/api'

/**
 * Full-page gate shown after login when an admin set a temporary password
 * (users.must_change_password). The server also blocks every other API call until
 * the password is changed, so this cannot be bypassed from the UI.
 */
const ForcePasswordChange = () => {
  const { user, login, logout } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (next.length < 8) return toast.error('New password must be at least 8 characters')
    if (next !== confirm) return toast.error('Passwords do not match')
    if (next === current) return toast.error('New password must be different from the temporary password')
    setSaving(true)
    try {
      const r = await authFetch(getLocalApiUrl('/api/account/change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to change password')
      // Re-authenticate with the new password to get a normal (unrestricted) session.
      const res = await login({ username: user?.username, password: next })
      if (res?.mfaRequired) {
        toast.success('Password updated. Please sign in again.')
        logout()
        return
      }
      if (!res?.success) throw new Error(res?.error || 'Password updated, but sign-in failed. Please sign in again.')
      toast.success('Password updated')
      // Full reload so every context starts from the fresh, unrestricted session.
      window.location.replace('/')
    } catch (err) {
      toast.error(err.message || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#0A5169' }}>
      <form onSubmit={submit} className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Set a new password</h2>
          <p className="mt-1 text-sm text-gray-600">
            Your account was created with a temporary password. Choose your own password to continue.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fpc-current">Temporary password</label>
          <input id="fpc-current" type="password" autoComplete="current-password" className="input-field" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fpc-next">New password</label>
          <input id="fpc-next" type="password" autoComplete="new-password" className="input-field" value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="mt-1 text-xs text-gray-500">At least 8 characters.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fpc-confirm">Confirm new password</label>
          <input id="fpc-confirm" type="password" autoComplete="new-password" className="input-field" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <div className="flex justify-between items-center pt-2">
          <button type="button" className="btn-secondary" onClick={logout}>Sign out</button>
          <button type="submit" className="btn-primary" disabled={saving || !current || !next || !confirm}>
            {saving ? 'Saving…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ForcePasswordChange
